import { MutableRefObject, useEffect, useMemo, useState } from 'react';
import { useAbilityContext } from '../../../../../../Can/Can';
import { Action } from '../../../../../../../enums/action';
import { PAGE_TITLE } from '../../../../../../../constants/common';
import { useAppDispatch, useAppSelector } from '../../../../../../../store/store';
import { ICustomChannelName } from '../../../../../../../types/channel-types';
import { IColorsDevices, IExcludeChannelsFormValues, IExcludedChannelNames } from './ReportChannelsModal';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { CHARACTER_SEPARATOR_IOT_KEY_NAME, NOTE_ID, SET_POINT_NAME } from '../../../../../../CycleChartPreview/constants';
import { DeviceColor } from '../../../../../../CycleChartPreview/types';
import ReactECharts from 'echarts-for-react';
import { SeriesOption } from 'echarts';
import { Cycle } from '../../../../../../../store/models/cycles';

export interface IReportChannelsInteractor {
  excludedChannelNames: IExcludedChannelNames[];
  channelNames: ICustomChannelName[];
  colorsDevices: IColorsDevices[];
  onSubmitFormHandler: (values: IExcludeChannelsFormValues) => Promise<void>;
  canEdit: boolean
}

export interface ReportChannelsInteractorProps {
  cycleId: string;
  cycle: Cycle;
  chartRef: MutableRefObject<ReactECharts>;
}

const buildColorArray = (
  deviceList: string[],
  stored: DeviceColor[],
  seriesMapByDevice: Record<string, string | string[]>,
): DeviceColor[] => {
  return deviceList.map((device) => {
    const storedColor = stored?.find((item) => Object.keys(item)[0] === device);
    const chartColor = seriesMapByDevice[device];
    return storedColor ?? ((chartColor ? { [device]: chartColor } : { [device]: null }) as DeviceColor);
  });
};

const getColorsDevicesByChart = (
  chartRef: MutableRefObject<ReactECharts>,
  colorsDevices: IColorsDevices[],
  cycle: Cycle,
): IColorsDevices[] => {
  if (!cycle) return colorsDevices;

  const chartInstance = chartRef?.current?.getEchartsInstance();
  if (!chartInstance) return colorsDevices;

  const series = chartInstance.getOption().series as SeriesOption[];

  const colors = cycle.cycle_controllers.map((cc) => {
    const controllerSerialNumber = cc.controller.serial_number;
    const controllerId = cc.controllerId;

    const seriesMapByDevice: Record<string, string | string[]> = {};

    series.forEach((ser) => {
      const isNote = String(ser.name).startsWith(NOTE_ID);
      if (ser.id === SET_POINT_NAME || !ser.color || isNote) return;

      const [serial, device] = String(ser.id).split(CHARACTER_SEPARATOR_IOT_KEY_NAME);
      if (serial === controllerSerialNumber) {
        seriesMapByDevice[device] = ser.color as string | string[];
      }
    });

    const storedColors = colorsDevices.find((item) => item.controllerId === controllerId);

    const channelColors = buildColorArray(cc.channels, storedColors?.channelColors ?? [], seriesMapByDevice);
    const thermocoupleColors = buildColorArray(
      cc.thermocouples,
      storedColors?.thermocoupleColors ?? [],
      seriesMapByDevice,
    );

    return {
      controllerId,
      channelColors,
      thermocoupleColors,
    };
  });

  return colors;
};

export const useReportChannelsInteractor = (props: ReportChannelsInteractorProps): IReportChannelsInteractor => {
  const { cycleId, cycle, chartRef } = props;

  const [channelNames, setChannelNames] = useState<ICustomChannelName[] | null>(null);
  const [colorsDevices, setColorsDevices] = useState<IColorsDevices[] | null>(null);

  const {
    reports: { getCustomChannelNames },
    cycles: {
      updateDeviceColorsReport,
      updateExcludeChannelNames,
      getColorDevices,
      getExcludeChannelNames,
      resetExcludedChannelsNames,
      resetColorDevices,
    },
  } = useAppDispatch();

  const { colorDevices, excludedChannelsNames } = useAppSelector((state) => state.cycles);

  const { t } = useTranslation();

  const ability = useAbilityContext()
  const canEdit = useMemo(()=>ability.can(Action.Read, PAGE_TITLE.EDIT_CYCLE), [ability])

  const updateColorByDevice = async (cycleId: string, listData: IColorsDevices[]) => {
    const modifiedData = listData.map((item) => {
      const { controllerId } = item;

      const channelColors = item.channelColors.filter((deviceWithColor) => Object.values(deviceWithColor)[0] !== null);
      const thermocoupleColors = item.thermocoupleColors.filter(
        (deviceWithColor) => Object.values(deviceWithColor)[0] !== null,
      );

      return {
        controllerId,
        channelColors,
        thermocoupleColors,
      };
    });

    await updateDeviceColorsReport({ cycleId, data: modifiedData });

    return;
  };

  const getData = async (cycleId: string) => {
    const names = await getCustomChannelNames(cycleId);
    await getColorDevices(cycleId);
    await getExcludeChannelNames(cycleId);

    setChannelNames(names);
  };

  const onSubmitFormHandler = async (values: IExcludeChannelsFormValues) => {
    const { exclusions, colors } = values;
    await updateExcludeChannelNames({ cycleId, data: exclusions });
    await updateColorByDevice(cycleId, colors);
    await getColorDevices(cycleId);

    await getExcludeChannelNames(cycleId);
    toast.success(t('messages.updateExcludeChannelSuccess'));
  };

  useEffect(() => {
    if (cycleId) {
      getData(cycleId);
    }
    return () => {
      resetColorDevices();
      resetExcludedChannelsNames();
    };
  }, [cycleId]);

  useEffect(() => {
    const colors = getColorsDevicesByChart(chartRef, colorDevices, cycle);
    setColorsDevices(colors);
  }, [colorDevices, cycle]);

  return {
    channelNames,
    excludedChannelNames: excludedChannelsNames,
    colorsDevices,
    canEdit,

    onSubmitFormHandler,
  };
};
